import type { EnvironmentId, PullRequestRef, PullRequestReviewThread } from "@cmmd-forge/contracts";
import { useState } from "react";

import { MarkdownEditor } from "~/components/rich-content/MarkdownEditor";
import { Button } from "~/components/ui/button";
import { cmmdApiClient } from "~/environments/cmmdApi";
import { commandPullRequestRef } from "~/lib/pullRequestReactQuery";
import { usePullRequestHandoffController } from "~/pullRequest/handoff/PullRequestHandoffProvider";
import { formatRelativeTimeLabel } from "~/timestampFormat";
import { PullRequestReactionPills } from "./PullRequestReactionPills";
import { PullRequestMarkdown } from "../PullRequestMarkdown";
import {
  canSubmitPullRequestThreadReply,
  PULL_REQUEST_REPLY_MAX_LENGTH,
  pullRequestThreadReplyError,
} from "./pullRequestThreadPagination.logic";
import { usePullRequestActivityCommand } from "./usePullRequestActivityCommand";
import { usePullRequestThreadPagination } from "./usePullRequestThreadPagination";

interface ReviewThreadProps {
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
  readonly thread: PullRequestReviewThread;
  readonly canReply: boolean;
  readonly canResolve: boolean;
  readonly reactionsEnabled: boolean;
}

export function PullRequestReviewThreadRow(props: ReviewThreadProps) {
  const [reply, setReply] = useState("");
  const pagination = usePullRequestThreadPagination(
    props.environmentId,
    props.reference,
    props.thread,
  );
  const command = usePullRequestActivityCommand(props.environmentId, props.reference);
  const handoff = usePullRequestHandoffController();
  return (
    <article className="rounded-md border border-border/60 p-3">
      <ReviewThreadHeader thread={props.thread} />
      <ReviewThreadComments {...props} comments={pagination.comments} command={command} />
      <ReviewThreadLoadMore pagination={pagination} />
      <ReviewThreadResolution {...props} command={command} />
      <ReviewThreadFixAction handoff={handoff} thread={props.thread} />
      <ReviewThreadReply {...props} command={command} reply={reply} onReplyChange={setReply} />
      <ReviewThreadErrors command={command} pagination={pagination} />
    </article>
  );
}

function ReviewThreadFixAction(props: {
  readonly handoff: ReturnType<typeof usePullRequestHandoffController>;
  readonly thread: PullRequestReviewThread;
}) {
  if (props.handoff === null) return null;
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="mt-3"
      disabled={props.handoff.pendingAction !== null}
      onClick={() =>
        void props.handoff?.run("fix-finding", { kind: "thread", thread: props.thread })
      }
    >
      {props.handoff.currentThreadMatches ? "Fix in this thread" : "Fix in a new thread"}
    </Button>
  );
}

function ReviewThreadHeader({ thread }: { readonly thread: PullRequestReviewThread }) {
  return (
    <p className="text-xs text-muted-foreground">
      {thread.path}
      {thread.line === null ? "" : `:${thread.line}`} ·{" "}
      {thread.isResolved ? "Resolved" : "Unresolved"}
      {thread.isOutdated ? " · Outdated" : ""}
    </p>
  );
}

function ReviewThreadComments(
  props: ReviewThreadProps & {
    readonly comments: PullRequestReviewThread["comments"];
    readonly command: ReturnType<typeof usePullRequestActivityCommand>;
  },
) {
  return props.comments.map((comment) => (
    <ReviewThreadComment key={comment.id} {...props} comment={comment} />
  ));
}

function ReviewThreadComment(
  props: ReviewThreadProps & {
    readonly comment: PullRequestReviewThread["comments"][number];
    readonly command: ReturnType<typeof usePullRequestActivityCommand>;
  },
) {
  return (
    <div className="mt-2">
      <p className="text-xs font-medium">
        {props.comment.author?.login ?? "Unknown author"} ·{" "}
        <span className="font-normal text-muted-foreground">
          {formatRelativeTimeLabel(props.comment.createdAt)}
        </span>
      </p>
      <PullRequestMarkdown text={props.comment.body} className="mt-1" />
      <PullRequestReactionPills
        reactions={props.comment.reactions}
        enabled={props.reactionsEnabled}
        pending={props.command.isPending}
        onToggle={(content, reacted) =>
          props.command.mutate(async () => {
            await cmmdApiClient.setPullRequestReaction({
              ...commandPullRequestRef(props.reference),
              idempotencyKey: crypto.randomUUID(),
              subjectId: props.comment.id,
              content,
              reacted,
            });
          })
        }
      />
    </div>
  );
}

function ReviewThreadLoadMore({
  pagination,
}: {
  readonly pagination: ReturnType<typeof usePullRequestThreadPagination>;
}) {
  return pagination.hasMore ? (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="mt-3"
      disabled={pagination.loadingMore}
      onClick={pagination.loadMore}
    >
      {pagination.loadingMore ? "Loading replies…" : "Load older replies"}
    </Button>
  ) : null;
}

function ReviewThreadResolution(
  props: ReviewThreadProps & {
    readonly command: ReturnType<typeof usePullRequestActivityCommand>;
  },
) {
  return props.canResolve ? (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="mt-3"
      disabled={props.command.isPending}
      onClick={() =>
        props.command.mutate(async () => {
          await cmmdApiClient.setPullRequestThreadResolution({
            ...commandPullRequestRef(props.reference),
            idempotencyKey: crypto.randomUUID(),
            threadId: props.thread.id,
            resolved: !props.thread.isResolved,
          });
        })
      }
    >
      {props.thread.isResolved ? "Reopen thread" : "Resolve thread"}
    </Button>
  ) : null;
}

function ReviewThreadReply(
  props: ReviewThreadProps & {
    readonly command: ReturnType<typeof usePullRequestActivityCommand>;
    readonly reply: string;
    readonly onReplyChange: (value: string) => void;
  },
) {
  if (!props.canReply) return null;
  const error = pullRequestThreadReplyError(props.reply);
  return (
    <div className="mt-3 space-y-2">
      <MarkdownEditor
        value={props.reply}
        ariaLabel="Reply to review thread"
        placeholder="Reply to thread"
        density="compact"
        maxLength={PULL_REQUEST_REPLY_MAX_LENGTH}
        disabled={props.command.isPending}
        onChange={props.onReplyChange}
      />
      <Button
        type="button"
        size="sm"
        disabled={!canSubmitPullRequestThreadReply(props.reply, props.command.isPending)}
        onClick={() => submitThreadReply(props)}
      >
        Reply
      </Button>
      {error === null ? null : (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

function submitThreadReply(
  props: ReviewThreadProps & {
    readonly command: ReturnType<typeof usePullRequestActivityCommand>;
    readonly reply: string;
    readonly onReplyChange: (value: string) => void;
  },
) {
  const body = props.reply.trim();
  props.command.mutate(
    async () => {
      await cmmdApiClient.replyToPullRequestThread({
        ...commandPullRequestRef(props.reference),
        idempotencyKey: crypto.randomUUID(),
        threadId: props.thread.id,
        body,
      });
    },
    () => props.onReplyChange(""),
  );
}

function ReviewThreadErrors(props: {
  readonly command: ReturnType<typeof usePullRequestActivityCommand>;
  readonly pagination: ReturnType<typeof usePullRequestThreadPagination>;
}) {
  return (
    <>
      {props.pagination.loadError ? (
        <p className="mt-2 text-sm text-destructive-foreground">
          Older replies could not be loaded: {props.pagination.loadError.message}
        </p>
      ) : null}
      {props.command.error ? (
        <p className="mt-2 text-sm text-destructive-foreground">
          Thread update failed: {props.command.error.message}
        </p>
      ) : null}
    </>
  );
}
